/**
 * Visualizer3D - Main orchestrator for 3D visualization
 * 
 * This class coordinates between specialized renderers and handlers:
 * - WaypointRenderer: Waypoint mesh creation and styling
 * - FeatureRenderer: Feature coordinate frame visualization
 * - RobotModelManager: Robot model loading and control
 * - InteractionHandler: Click detection and selection
 * - CameraController: Camera positioning and animation
 * - MirrorPlaneHelper: Mirror plane visualization
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { WaypointRenderer } from './WaypointRenderer.js';
import { FeatureRenderer } from './FeatureRenderer.js';
import { RobotModelManager } from './RobotModelManager.js';
import { InteractionHandler } from './InteractionHandler.js';
import { CameraController } from './CameraController.js';
import { MirrorPlaneHelper } from './helpers/MirrorPlaneHelper.js';
import { LabelFactory } from './helpers/LabelFactory.js';

export class Visualizer3D {
  constructor(container) {
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    
    // Specialized managers
    this.waypointRenderer = null;
    this.featureRenderer = null;
    this.robotManager = null;
    this.interactionHandler = null;
    this.cameraController = null;
    this.mirrorPlaneHelper = null;
    
    // State
    this.waypoints = [];
    this.selectedWaypoint = null;
    this.labelsVisible = true;
    this.sphereSizeMultiplier = 1.0;
    this.featuresVisible = false;
    
    // Legacy properties for backward compatibility
    this.waypointMeshes = [];
    this.viaPointMeshes = [];
    this.featureMeshes = [];
    this.pathLines = [];
    this.features = [];
    
    // Robot properties for backward compatibility
    this.ur10 = null;
    this.robotModel = null;
    this.robotVisible = true;
    this.robotLoaded = false;
    this.pendingRobotType = null;
    
    // Callbacks
    this.onWaypointClick = null;
    this.onViaPointClick = null;
    this.onFeatureClick = null;
    this.onDeselect = null;
    
    this.init();
  }

  /**
   * Initialize the Three.js scene and all managers
   */
  init() {
    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a1a);

    // Create camera
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
    this.camera.position.set(2, 2, 2);
    this.camera.lookAt(0, 0, 0);

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Add orbit controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 0.5;
    this.controls.maxDistance = 50;

    // Add lights
    this.setupLights();

    // Add grid and axes helpers
    this.addHelpers();

    // Initialize specialized managers
    this.waypointRenderer = new WaypointRenderer(this.scene, {
      labelsVisible: this.labelsVisible,
      sphereSizeMultiplier: this.sphereSizeMultiplier
    });
    
    this.featureRenderer = new FeatureRenderer(this.scene);
    this.robotManager = new RobotModelManager(this.scene);
    this.cameraController = new CameraController(this.camera, this.controls);
    this.mirrorPlaneHelper = new MirrorPlaneHelper(this.scene);
    
    // Setup interaction handler
    this.interactionHandler = new InteractionHandler(this.renderer, this.camera);
    this.setupInteractionCallbacks();

    // Load robot model by default
    this.addRobotModel('ur10e');

    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());

    // Start animation loop
    this.animate();
  }

  /**
   * Setup interaction handler callbacks
   */
  setupInteractionCallbacks() {
    this.interactionHandler.onWaypointClick = (index) => {
      this.selectWaypoint(index);
    };
    
    this.interactionHandler.onViaPointClick = (waypointIndex, userData) => {
      this.selectViaPoint(waypointIndex);
      if (this.onViaPointClick) {
        this.onViaPointClick(waypointIndex, userData);
      }
    };
    
    this.interactionHandler.onFeatureClick = (index) => {
      if (this.onFeatureClick) {
        this.onFeatureClick(index);
      }
    };
    
    this.interactionHandler.onDeselect = () => {
      this.deselectWaypoint();
    };
  }

  /**
   * Setup scene lighting
   */
  setupLights() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    this.scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0x4466ff, 0.3);
    fillLight.position.set(-5, 5, -5);
    this.scene.add(fillLight);
  }

  /**
   * Add grid and coordinate axes helpers
   */
  addHelpers() {
    // Grid helper
    const gridHelper = new THREE.GridHelper(4, 20, 0x444444, 0x222222);
    this.scene.add(gridHelper);

    // Create custom axes
    // X-axis (red)
    const xAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(1, 0, 0)
    ]);
    const xAxisMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 });
    this.scene.add(new THREE.Line(xAxisGeometry, xAxisMaterial));

    // Y-axis (green, pointing in -Z direction)
    const yAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, -1)
    ]);
    const yAxisMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
    this.scene.add(new THREE.Line(yAxisGeometry, yAxisMaterial));

    // Z-axis (blue, pointing up)
    const zAxisGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 1, 0)
    ]);
    const zAxisMaterial = new THREE.LineBasicMaterial({ color: 0x0000ff });
    this.scene.add(new THREE.Line(zAxisGeometry, zAxisMaterial));

    // Add axis labels
    this.scene.add(LabelFactory.createAxisLabel('X', '#ff0000', new THREE.Vector3(1.2, 0, 0)));
    this.scene.add(LabelFactory.createAxisLabel('Y', '#00ff00', new THREE.Vector3(0, 0, -1.2)));
    this.scene.add(LabelFactory.createAxisLabel('Z', '#0000ff', new THREE.Vector3(0, 1.2, 0)));
  }

  /**
   * Add robot model to the scene
   * @param {string} robotType - Robot type (ur10e, ur20, etc.)
   */
  async addRobotModel(robotType = 'ur10e') {
    await this.robotManager.addRobotModel(robotType);
    
    // Update legacy properties for backward compatibility
    this.ur10 = this.robotManager.getRobot();
    this.robotModel = this.robotManager.robotModel;
    this.robotLoaded = this.robotManager.robotLoaded;
    this.pendingRobotType = robotType;
  }

  /**
   * Toggle robot visibility
   */
  toggleRobotVisibility() {
    this.robotManager.toggleVisibility();
    this.robotVisible = this.robotManager.robotVisible;
  }

  /**
   * Set robot visibility
   * @param {boolean} visible - Whether robot should be visible
   */
  setRobotVisible(visible) {
    this.robotManager.setVisible(visible);
    this.robotVisible = visible;
  }

  /**
   * Set the robot type
   * @param {string} robotType - Robot type (ur10e, ur20, etc.)
   */
  setRobotType(robotType) {
    this.robotManager.setRobotType(robotType);
    this.pendingRobotType = robotType;
  }

  /**
   * Visualize waypoints from parsed data
   * @param {Array<Object>} waypoints - Array of waypoint objects
   * @param {boolean} centerCamera - Whether to center camera on waypoints
   */
  visualizeWaypoints(waypoints, centerCamera = true) {
    this.clearVisualization();
    this.waypoints = waypoints;

    if (!waypoints || waypoints.length === 0) return;

    // Calculate average distance for adaptive sizing
    const avgDistance = this.waypointRenderer.calculateAverageDistance(waypoints);

    // Create waypoint and via-point meshes
    waypoints.forEach((waypoint, index) => {
      if (!waypoint || !waypoint.position) {
        console.error(`Invalid waypoint at index ${index}:`, waypoint);
        return;
      }
      this.waypointRenderer.createWaypointMesh(waypoint, index, avgDistance);
      
      if (waypoint.viaPoint) {
        this.waypointRenderer.createViaPointMesh(waypoint.viaPoint, index, waypoint, avgDistance);
      }
    });

    // Draw paths
    this.waypointRenderer.drawPaths(waypoints);

    // Update legacy arrays for backward compatibility
    this.waypointMeshes = this.waypointRenderer.waypointMeshes;
    this.viaPointMeshes = this.waypointRenderer.viaPointMeshes;
    this.pathLines = this.waypointRenderer.pathLines;

    // Update interaction handler
    this.interactionHandler.updateMeshSources({
      waypointMeshes: this.waypointMeshes,
      viaPointMeshes: this.viaPointMeshes,
      featureMeshes: this.featureMeshes
    });

    // Center camera
    if (centerCamera) {
      setTimeout(() => this.centerCamera(), 100);
    }
  }

  /**
   * Visualize features (coordinate frames)
   * @param {Array<Object>} features - Array of feature objects
   */
  visualizeFeatures(features) {
    this.featureRenderer.visualize(features);
    this.features = features;
    this.featureMeshes = this.featureRenderer.featureMeshes;
    
    // Update interaction handler
    this.interactionHandler.updateMeshSources({
      waypointMeshes: this.waypointMeshes,
      viaPointMeshes: this.viaPointMeshes,
      featureMeshes: this.featureMeshes
    });
  }

  /**
   * Select a waypoint
   * @param {number} index - Index of waypoint to select
   * @param {boolean} triggerCallback - Whether to trigger the click callback
   */
  selectWaypoint(index, triggerCallback = true) {
    // Deselect previous
    this.deselectWaypoint(false);

    if (index >= 0 && index < this.waypointMeshes.length) {
      this.selectedWaypoint = index;
      
      // Highlight waypoint
      this.waypointRenderer.highlightWaypoint(index);

      // Also highlight via-point if exists
      const waypoint = this.waypoints[index];
      if (waypoint && waypoint.viaPoint) {
        this.waypointRenderer.highlightViaPoint(index);
      }

      // Trigger callback
      if (triggerCallback && this.onWaypointClick) {
        this.onWaypointClick(this.waypoints[index], index);
      }
    }
  }

  /**
   * Select a via-point and its parent waypoint
   * @param {number} waypointIndex - Index of the parent waypoint
   */
  selectViaPoint(waypointIndex) {
    this.deselectWaypoint(false);

    if (waypointIndex >= 0 && waypointIndex < this.waypoints.length) {
      this.selectedWaypoint = waypointIndex;
      
      this.waypointRenderer.highlightWaypoint(waypointIndex);
      this.waypointRenderer.highlightViaPoint(waypointIndex);
    }
  }

  /**
   * Deselect current waypoint
   * @param {boolean} triggerCallback - Whether to trigger the deselect callback
   */
  deselectWaypoint(triggerCallback = true) {
    if (this.selectedWaypoint !== null && this.selectedWaypoint < this.waypointMeshes.length) {
      this.waypointRenderer.unhighlightWaypoint(this.selectedWaypoint);

      const waypoint = this.waypoints[this.selectedWaypoint];
      if (waypoint && waypoint.viaPoint) {
        this.waypointRenderer.unhighlightViaPoint(this.selectedWaypoint);
      }
    }
    this.selectedWaypoint = null;

    if (triggerCallback && this.onDeselect) {
      this.onDeselect();
    }
  }

  /**
   * Update a waypoint's position in the visualization
   * @param {number} index - Waypoint index
   * @param {Object} position - New position {x, y, z}
   */
  updateWaypointPosition(index, position) {
    if (index >= 0 && index < this.waypointMeshes.length) {
      const mesh = this.waypointMeshes[index];
      mesh.position.set(position.x, position.z, -position.y);

      // Redraw paths
      this.waypointRenderer.clearPaths();
      this.waypointRenderer.drawPaths(this.waypoints);
      this.pathLines = this.waypointRenderer.pathLines;
    }
  }

  /**
   * Center camera on waypoints
   */
  centerCamera() {
    if (this.waypointMeshes.length === 0) return;
    this.cameraController.centerOnWaypoints(this.waypointMeshes);
  }

  /**
   * Focus camera on a specific waypoint
   * @param {number} index - Index of waypoint to focus on
   */
  focusOnWaypoint(index) {
    if (index < 0 || index >= this.waypointMeshes.length) return;
    this.cameraController.focusOnMesh(this.waypointMeshes[index]);
  }

  /**
   * Clear all waypoint visualizations
   */
  clearVisualization() {
    this.waypointRenderer.clear();
    this.waypointMeshes = [];
    this.viaPointMeshes = [];
    this.pathLines = [];
    this.selectedWaypoint = null;
  }

  /**
   * Clear paths only
   */
  clearPaths() {
    this.waypointRenderer.clearPaths();
    this.pathLines = [];
  }

  /**
   * Update sphere size multiplier
   * @param {number} multiplier - Size multiplier
   */
  setSphereSize(multiplier) {
    this.sphereSizeMultiplier = multiplier;
    this.waypointRenderer.setSphereSize(multiplier);

    // Re-visualize with new size
    if (this.waypoints.length > 0) {
      const waypointsForVis = this.waypoints;
      this.clearVisualization();
      this.visualizeWaypoints(waypointsForVis, false);
    }
  }

  /**
   * Toggle waypoint labels visibility
   * @param {boolean} visible - Whether labels should be visible
   */
  toggleLabels(visible) {
    this.labelsVisible = visible;
    this.waypointRenderer.setLabelsVisible(visible);
  }

  /**
   * Toggle feature visibility
   * @param {boolean} visible - Whether features should be visible
   */
  setFeaturesVisible(visible) {
    this.featuresVisible = visible;
    this.featureRenderer.setVisible(visible);
  }

  /**
   * Clear all feature visualizations
   */
  clearFeatures() {
    this.featureRenderer.clear();
    this.featureMeshes = [];
    this.features = [];
  }

  /**
   * Set robot joint angles
   * @param {Array<number>} angles - Array of 6 joint angles in degrees
   */
  setRobotJointAngles(angles) {
    this.robotManager.setJointAngles(angles);
  }

  /**
   * Show mirror plane
   * @param {Object} feature - Feature to show the plane at
   * @param {string} plane - Plane type ('xy', 'xz', or 'yz')
   */
  showMirrorPlane(feature, plane) {
    this.mirrorPlaneHelper.show(feature, plane);
  }

  /**
   * Hide mirror plane
   */
  hideMirrorPlane() {
    this.mirrorPlaneHelper.hide();
  }

  /**
   * Handle window resize
   */
  onWindowResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  /**
   * Animation loop
   */
  animate() {
    requestAnimationFrame(() => this.animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.clearVisualization();
    this.clearFeatures();
    this.robotManager.dispose();
    this.mirrorPlaneHelper.dispose();
    this.interactionHandler.dispose();
    this.controls.dispose();
    this.renderer.dispose();
    window.removeEventListener('resize', this.onWindowResize);
  }
}
